import request from '@/Network/Requests/request'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import Service from '@/Network/Services/Service'
import type {
  PublishTermVersionRequest,
  TermSignaturesQuery,
  TermVersionsQuery
} from '@/Network/Types/Requests/TermRequests'
import type {
  TermModality,
  TermPublicationImpactResponse,
  TermSignaturesPageResponse,
  TermsOverviewResponse,
  TermVersionDetail,
  TermVersionsPageResponse
} from '@/Network/Types/Responses/TermResponses'

const CSV_CONTENT_TYPE = 'text/csv;charset=utf-8'
/** Nome com aspas (pode ter espaço) ou sem aspas (vai até o próximo `;`). */
const FILENAME = /filename="([^"]+)"|filename=([^;\s]+)/

type QueryValue = string | number | undefined

/** Query string só com os campos definidos, em texto. */
const definedParams = (query: Record<string, QueryValue>): Record<string, string> => {
  const params: Record<string, string> = {}
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params[key] = String(value)
  })
  return params
}

/**
 * Termos de ciência (PRD26), telas de admin, nas rotas `/admin/terms` do
 * backend (super admin ou administrador geral de qualquer empresa, G25). O e2e roda contra o mock em
 * `tests/e2e/helpers/terms-mock-api.ts`, que segue o mesmo contrato.
 */
export default class TermService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/admin/terms'
  }

  async getOverview(): Promise<TermsOverviewResponse> {
    try {
      const { data } = await request.get<TermsOverviewResponse>(this.getCompletePath())
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os termos de ciência')
    }
  }

  async listVersions(query: TermVersionsQuery): Promise<TermVersionsPageResponse> {
    try {
      const { data } = await request.get<TermVersionsPageResponse>(
        this.getCompletePath('/versions'),
        definedParams({ ...query })
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o histórico de versões')
    }
  }

  async getVersion(id: number): Promise<TermVersionDetail> {
    try {
      const { data } = await request.get<TermVersionDetail>(this.getCompletePath(`/versions/${id}`))
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a versão do termo')
    }
  }

  async listSignatures(query: TermSignaturesQuery): Promise<TermSignaturesPageResponse> {
    try {
      const { data } = await request.get<TermSignaturesPageResponse>(
        this.getCompletePath('/acceptances'),
        definedParams({ ...query })
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as assinaturas do termo')
    }
  }

  async getPublicationImpact(modality: TermModality): Promise<TermPublicationImpactResponse> {
    try {
      const { data } = await request.get<TermPublicationImpactResponse>(
        this.getCompletePath(`/${modality}/publication-impact`)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível calcular quantos médicos serão afetados')
    }
  }

  async publish(
    modality: TermModality,
    payload: PublishTermVersionRequest
  ): Promise<TermVersionDetail> {
    try {
      const { data } = await request.post<TermVersionDetail>(
        this.getCompletePath(`/${modality}/versions`),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível publicar a nova versão do termo')
    }
  }

  /**
   * CSV das assinaturas de uma versão, no padrão de `AnnouncementService.exportXlsx`.
   * Recortado pela versão para não passar do limite de linhas do backend.
   */
  async exportSignatures(termVersionId: number): Promise<void> {
    try {
      const { data, headers } = await request.get<BlobPart>(
        this.getCompletePath('/acceptances/export'),
        definedParams({ termVersionId }),
        undefined,
        'blob'
      )
      const disposition = (headers?.['content-disposition'] as string | undefined) ?? ''
      const match = disposition.match(FILENAME)
      const filename = match?.[1] ?? match?.[2] ?? 'termos-aceites.csv'
      const url = window.URL.createObjectURL(new Blob([data], { type: CSV_CONTENT_TYPE }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      throw this.handleError(await unwrapBlobError(e), 'Não foi possível exportar as assinaturas')
    }
  }
}
